import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, CheckCircle2, Clock, PackageCheck, Plus, RefreshCw, Search, UserCheck, X } from 'lucide-react';
import { toast } from 'sonner';

import { clientsApi } from '@/api/clients';
import {
  licensingApi,
  type ApprovedGuestDownloadRequestRecord,
  type GuestDownloadRequestRecord,
  type ReleaseDownloadCatalogItemRecord,
} from '@/api/licensing';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { isMailArchiverApp, MailArchiverIcon } from './MailArchiverIcon';
import {
  buildInstallerCatalog,
  filterInstallerApps,
  releaseCatalogItemLabel,
  type InstallerAppGroup,
  type InstallerPlatformGroup,
} from './releaseDownloadCatalogModel';

function formatDateTime(value?: string): string {
  if (!value) return 'нет данных';
  return new Date(value).toLocaleString('ru-RU');
}

function formatAge(value?: string): string {
  if (!value) return 'нет данных';
  const created = new Date(value).getTime();
  if (!Number.isFinite(created)) return formatDateTime(value);
  const seconds = Math.max(0, Math.floor((Date.now() - created) / 1000));
  if (seconds < 60) return 'только что';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} мин назад`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ч назад`;
  return `${Math.floor(hours / 24)} дн назад`;
}

function formatBytes(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '0 Б';
  const units = ['Б', 'КБ', 'МБ', 'ГБ'];
  let size = value;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
}

function statusTone(status: string): 'visit' | 'deadline' | 'today' | 'outline' {
  if (status === 'APPROVED') return 'visit';
  if (status === 'EXPIRED' || status === 'REVOKED') return 'deadline';
  if (status === 'PENDING') return 'today';
  return 'outline';
}

export function GuestDownloadApprovalPanel({
  fixedClientId,
  clientName,
}: {
  fixedClientId?: number;
  clientName?: string;
}) {
  const queryClient = useQueryClient();
  const [requestCode, setRequestCode] = useState('');
  const [pendingSearch, setPendingSearch] = useState('');
  const [selectedClientId, setSelectedClientId] = useState(fixedClientId ? String(fixedClientId) : '');
  const [selectedAppId, setSelectedAppId] = useState('');
  const [appSearch, setAppSearch] = useState('');
  const [selectedPlatformKey, setSelectedPlatformKey] = useState('');
  const [selectedVersionArtifactId, setSelectedVersionArtifactId] = useState('');
  const [selectedArtifactIds, setSelectedArtifactIds] = useState<string[]>([]);
  const [seededSelectionKey, setSeededSelectionKey] = useState('');
  const [ttlHours, setTtlHours] = useState('24');
  const [resolved, setResolved] = useState<GuestDownloadRequestRecord | null>(null);
  const [approved, setApproved] = useState<ApprovedGuestDownloadRequestRecord | null>(null);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [revokeReason, setRevokeReason] = useState('Выдано ошибочно');

  const effectiveClientId = fixedClientId ?? Number(selectedClientId || 0);

  useEffect(() => {
    if (fixedClientId) {
      setSelectedClientId(String(fixedClientId));
    }
  }, [fixedClientId]);

  useEffect(() => {
    setSelectedArtifactIds([]);
    setSeededSelectionKey('');
    setSelectedPlatformKey('');
    setSelectedVersionArtifactId('');
  }, [effectiveClientId]);

  const clients = useQuery({
    queryKey: ['licensing', 'clients-for-guest-download-approval'],
    queryFn: () => clientsApi.getForSelector(),
    enabled: !fixedClientId,
  });
  const normalizedPendingSearch = normalizeCode(pendingSearch);
  const pendingRequests = useQuery({
    queryKey: ['licensing', 'pending-guest-download-requests', normalizedPendingSearch],
    queryFn: () =>
      licensingApi.listGuestDownloadRequests({
        status: 'PENDING',
        search: normalizedPendingSearch || undefined,
        limit: 5,
      }),
    refetchInterval: 10000,
  });
  const catalog = useQuery({
    queryKey: ['licensing', 'guest-download-catalog', effectiveClientId],
    queryFn: () => licensingApi.listClientReleaseDownloadCatalog(effectiveClientId),
    enabled: effectiveClientId > 0,
  });

  const catalogItems = catalog.data?.items ?? [];
  const installerApps = useMemo(() => buildInstallerCatalog(catalogItems), [catalogItems]);
  const filteredApps = useMemo(() => filterInstallerApps(installerApps, appSearch), [appSearch, installerApps]);
  const selectedApp = useMemo(
    () => installerApps.find((app) => app.appId === selectedAppId) ?? installerApps[0] ?? null,
    [installerApps, selectedAppId],
  );
  const selectedPlatform = useMemo(
    () => selectedApp?.platforms.find((platform) => platform.key === selectedPlatformKey) ?? selectedApp?.platforms[0] ?? null,
    [selectedApp, selectedPlatformKey],
  );
  const explicitVersion = useMemo(
    () => selectedPlatform?.items.find((item) => item.artifact.id === selectedVersionArtifactId) ?? null,
    [selectedPlatform, selectedVersionArtifactId],
  );
  const selectedArtifact = explicitVersion ?? selectedPlatform?.latest ?? null;
  const selectedArtifactSet = useMemo(() => new Set(selectedArtifactIds), [selectedArtifactIds]);
  const selectedItems = useMemo(
    () =>
      selectedArtifactIds
        .map((artifactId) => catalogItems.find((item) => item.artifact.id === artifactId))
        .filter((item): item is ReleaseDownloadCatalogItemRecord => Boolean(item)),
    [catalogItems, selectedArtifactIds],
  );
  const latestSelected = Boolean(
    selectedArtifact && selectedPlatform && selectedArtifact.artifact.id === selectedPlatform.latest.artifact.id,
  );

  const resolve = useMutation({
    mutationFn: () => licensingApi.resolveGuestDownloadRequest(normalizeCode(requestCode)),
    onSuccess: (request) => {
      setResolved(request);
      setApproved(null);
      setSelectedArtifactIds([]);
      setSeededSelectionKey('');
      toast.success('Guest request найден');
    },
    onError: (error) => {
      setResolved(null);
      setApproved(null);
      setSelectedArtifactIds([]);
      setSeededSelectionKey('');
      toast.error(error instanceof Error ? error.message : 'Не удалось найти guest request');
    },
  });

  const approve = useMutation({
    mutationFn: () => {
      const code = resolved?.requestCode || normalizeCode(requestCode);
      const input = {
        artifactIds: selectedArtifactIds,
        ttlHours: Number(ttlHours),
      };
      if (fixedClientId) {
        return licensingApi.approveClientGuestDownloadRequest(code, fixedClientId, input);
      }
      return licensingApi.approveGuestDownloadRequest(code, {
        ...input,
        clientId: effectiveClientId,
      });
    },
    onSuccess: (result) => {
      setApproved(result);
      setResolved(result);
      void queryClient.invalidateQueries({ queryKey: ['licensing', 'pending-guest-download-requests'] });
      toast.success('Guest downloads открыты');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Не удалось подтвердить скачивание');
    },
  });
  const revoke = useMutation({
    mutationFn: () =>
      licensingApi.revokeGuestDownloadRequest(resolved?.requestCode || normalizeCode(requestCode), revokeReason.trim()),
    onSuccess: (request) => {
      setResolved(request);
      setApproved(null);
      setRevokeOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['licensing', 'pending-guest-download-requests'] });
      toast.success('Guest downloads отозваны');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Не удалось отозвать скачивание');
    },
  });

  const canApprove =
    resolved?.status === 'PENDING' &&
    effectiveClientId > 0 &&
    selectedArtifactIds.length > 0 &&
    Number(ttlHours) >= 24 &&
    Number(ttlHours) <= 168;

  useEffect(() => {
    if (resolved?.status !== 'PENDING' || effectiveClientId <= 0 || !selectedArtifact) return;
    const key = `${resolved.requestCode}:${effectiveClientId}`;
    if (seededSelectionKey === key) return;
    setSelectedArtifactIds([selectedArtifact.artifact.id]);
    setSeededSelectionKey(key);
  }, [effectiveClientId, resolved?.requestCode, resolved?.status, seededSelectionKey, selectedArtifact]);

  const selectApp = (appId: string) => {
    setSelectedAppId(appId);
    setSelectedPlatformKey('');
    setSelectedVersionArtifactId('');
    setApproved(null);
  };

  const selectPlatform = (platformKeyValue: string) => {
    setSelectedPlatformKey(platformKeyValue);
    setSelectedVersionArtifactId('');
    setApproved(null);
  };

  const addSelectedArtifact = () => {
    if (!selectedArtifact) return;
    setSelectedArtifactIds((current) =>
      current.includes(selectedArtifact.artifact.id) ? current : [...current, selectedArtifact.artifact.id],
    );
    setApproved(null);
  };

  const removeSelectedArtifact = (artifactId: string) => {
    setSelectedArtifactIds((current) => current.filter((id) => id !== artifactId));
    setApproved(null);
  };

  const selectPendingRequest = (request: GuestDownloadRequestRecord) => {
    setRequestCode(request.requestCode);
    setResolved(request);
    setApproved(null);
    setSelectedArtifactIds([]);
    setSeededSelectionKey('');
  };

  return (
    <section className="rounded-lg border bg-card p-4" data-testid="guest-download-approval-panel">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
          <UserCheck className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold">Гостевой доступ</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {fixedClientId
              ? `${clientName ?? `Клиент ${fixedClientId}`}: подтверждение кода на скачивание`
              : 'Подтверждение кода гостя на скачивание установщика'}
          </p>
        </div>
      </div>

      <div className="mt-4 grid gap-3">
        <PendingGuestQueue
          requests={pendingRequests.data?.items ?? []}
          search={pendingSearch}
          loading={pendingRequests.isLoading}
          fetching={pendingRequests.isFetching}
          error={pendingRequests.error}
          onSearchChange={(value) => setPendingSearch(normalizeCode(value))}
          onRefresh={() => void pendingRequests.refetch()}
          onSelect={selectPendingRequest}
        />

        <div className="grid grid-cols-[1fr_auto] gap-2">
          <Input
            data-testid="guest-download-request-code-input"
            value={requestCode}
            onChange={(event) => {
              setRequestCode(normalizeCode(event.target.value));
              setResolved(null);
              setApproved(null);
              setSelectedArtifactIds([]);
              setSeededSelectionKey('');
            }}
            placeholder="Код гостя"
            className="font-mono uppercase"
            autoCapitalize="characters"
          />
          <Button
            type="button"
            variant="outline"
            aria-label="Найти гостевой код"
            data-testid="guest-download-request-resolve-button"
            disabled={normalizeCode(requestCode).length === 0 || resolve.isPending}
            onClick={() => resolve.mutate()}
          >
            <Search className="h-4 w-4" />
          </Button>
        </div>

        {resolve.isPending && <Skeleton className="h-20 w-full" />}

        {resolved && <RequestSummary request={resolved} />}

        {resolved?.status === 'PENDING' && (
          <div className="grid gap-3">
            {!fixedClientId && (
              <Select value={selectedClientId} onValueChange={setSelectedClientId} disabled={clients.isLoading || approve.isPending}>
                <SelectTrigger>
                  <SelectValue placeholder={clients.isLoading ? 'Загрузка клиентов...' : 'Выберите клиента'} />
                </SelectTrigger>
                <SelectContent>
                  {(clients.data ?? []).map((client) => (
                    <SelectItem key={client.id} value={String(client.id)}>
                      {client.name} [{client.id}]
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {clients.isError && !fixedClientId && (
              <div className="rounded-md border border-destructive/30 bg-background p-3 text-sm text-destructive">
                {clients.error instanceof Error ? clients.error.message : 'Не удалось загрузить клиентов'}
              </div>
            )}

            {effectiveClientId > 0 && catalog.isLoading && (
              <div className="grid gap-2">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            )}

            {catalog.isError && (
              <div className="rounded-md border border-destructive/30 bg-background p-3 text-sm text-destructive">
                {catalog.error instanceof Error ? catalog.error.message : 'Не удалось загрузить каталог'}
              </div>
            )}

            {effectiveClientId > 0 && !catalog.isLoading && !catalog.isError && catalogItems.length === 0 && (
              <div className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
                Нет опубликованных установщиков для выдачи.
              </div>
            )}

            {catalogItems.length > 0 && selectedApp && selectedPlatform && selectedArtifact && (
              <div className="grid gap-3">
                <GuestInstallerPicker
                  apps={installerApps}
                  filteredApps={filteredApps}
                  selectedAppId={selectedApp.appId}
                  appSearch={appSearch}
                  onAppSearchChange={setAppSearch}
                  onSelectApp={selectApp}
                  selectedPlatform={selectedPlatform}
                  selectedPlatformKey={selectedPlatform.key}
                  onSelectPlatform={selectPlatform}
                  selectedArtifact={selectedArtifact}
                  selectedArtifactAlreadyAdded={selectedArtifactSet.has(selectedArtifact.artifact.id)}
                  latestSelected={latestSelected}
                  onAddSelectedArtifact={addSelectedArtifact}
                  onSelectVersion={(artifactId) => {
                    setSelectedVersionArtifactId(artifactId === selectedPlatform.latest.artifact.id ? '' : artifactId);
                    setApproved(null);
                  }}
                />
                <SelectedInstallerList items={selectedItems} onRemove={removeSelectedArtifact} />
              </div>
            )}

            <Select value={ttlHours} onValueChange={setTtlHours}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="24">24 часа</SelectItem>
                <SelectItem value="72">3 дня</SelectItem>
                <SelectItem value="168">7 дней</SelectItem>
              </SelectContent>
            </Select>

            <Button
              type="button"
              disabled={!canApprove || approve.isPending}
              onClick={() => approve.mutate()}
              data-testid="guest-download-request-approve-button"
            >
              <CheckCircle2 className="h-4 w-4" />
              Разрешить скачивание
            </Button>
          </div>
        )}

        {selectedItems.length > 0 && !approved && (
          <div className="text-xs text-muted-foreground">
            Выбрано установщиков: {selectedItems.length}
          </div>
        )}

        {approved && (
          <div className="rounded-md border border-green-200 bg-green-50 p-3 text-green-950 dark:border-green-500/30 dark:bg-green-500/10 dark:text-green-100">
            <p className="text-sm font-medium">Гостю открыты скачивания</p>
            <p className="mt-1 text-xs opacity-80">
              До {formatDateTime(approved.expiresAt)} · установщиков: {approved.artifacts.length}
            </p>
          </div>
        )}

        {resolved?.status === 'APPROVED' && (
          <Button type="button" variant="outline" onClick={() => setRevokeOpen(true)} disabled={revoke.isPending}>
            <Ban className="h-4 w-4" />
            Отозвать гостевой доступ
          </Button>
        )}
      </div>

      <AlertDialog open={revokeOpen} onOpenChange={setRevokeOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Отозвать гостевой доступ</AlertDialogTitle>
            <AlertDialogDescription>
              После отзыва публичная страница перестанет показывать и скачивать разрешенные установщики.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            value={revokeReason}
            onChange={(event) => setRevokeReason(event.target.value)}
            placeholder="Причина отзыва"
            className="min-h-24"
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoke.isPending}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              disabled={!revokeReason.trim() || revoke.isPending}
              onClick={(event) => {
                event.preventDefault();
                revoke.mutate();
              }}
            >
              Отозвать
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function PendingGuestQueue({
  requests,
  search,
  loading,
  fetching,
  error,
  onSearchChange,
  onRefresh,
  onSelect,
}: {
  requests: GuestDownloadRequestRecord[];
  search: string;
  loading: boolean;
  fetching: boolean;
  error: unknown;
  onSearchChange: (value: string) => void;
  onRefresh: () => void;
  onSelect: (request: GuestDownloadRequestRecord) => void;
}) {
  const errorMessage = error instanceof Error ? error.message : 'Не удалось загрузить ожидающих гостей';

  return (
    <div className="grid gap-2 rounded-md border bg-background p-3" data-testid="pending-guest-download-queue">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium">Ожидают разрешение</p>
          <p className="text-xs text-muted-foreground">Последние 5 заявок на скачивание</p>
        </div>
        <Button type="button" variant="outline" size="icon" aria-label="Обновить список гостей" onClick={onRefresh} disabled={fetching}>
          <RefreshCw className={cn('h-4 w-4', fetching && 'animate-spin')} />
        </Button>
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Поиск по коду"
          className="pl-9 font-mono uppercase"
          autoCapitalize="characters"
          data-testid="pending-guest-download-search"
        />
      </div>

      {loading && (
        <div className="grid gap-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      )}

      {Boolean(error) && !loading && (
        <div className="rounded-md border border-destructive/30 bg-background p-3 text-sm text-destructive">
          {errorMessage}
        </div>
      )}

      {!loading && !error && requests.length === 0 && (
        <div className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
          {search ? 'По этому фрагменту pending-заявок нет.' : 'Сейчас нет ожидающих гостей.'}
        </div>
      )}

      {!loading && !error && requests.length > 0 && (
        <div className="grid gap-2">
          {requests.map((request) => (
            <button
              key={request.id}
              type="button"
              className="w-full rounded-md border bg-card p-3 text-left active:scale-[0.99]"
              data-testid="pending-guest-download-row"
              data-request-code={request.requestCode}
              onClick={() => onSelect(request)}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-base font-semibold tracking-normal">{request.requestCode}</span>
                    <Badge variant="today">{request.status}</Badge>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-2 gap-y-1 text-xs text-muted-foreground">
                    <span>создано {formatAge(request.createdAt)}</span>
                    <span>до {formatDateTime(request.expiresAt)}</span>
                  </div>
                </div>
                <Clock className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function RequestSummary({ request }: { request: GuestDownloadRequestRecord }) {
  return (
    <div className="rounded-md bg-muted p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={statusTone(request.status)}>{request.status}</Badge>
            <span className="font-mono text-xs text-muted-foreground">{request.requestCode}</span>
          </div>
          <p className="mt-2 text-sm">Действует до {formatDateTime(request.expiresAt)}</p>
          {request.clientId ? (
            <p className="mt-1 text-xs text-muted-foreground">Клиент {request.clientId}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function GuestInstallerPicker({
  apps,
  filteredApps,
  selectedAppId,
  appSearch,
  onAppSearchChange,
  onSelectApp,
  selectedPlatform,
  selectedPlatformKey,
  onSelectPlatform,
  selectedArtifact,
  selectedArtifactAlreadyAdded,
  latestSelected,
  onAddSelectedArtifact,
  onSelectVersion,
}: {
  apps: InstallerAppGroup[];
  filteredApps: InstallerAppGroup[];
  selectedAppId: string;
  appSearch: string;
  onAppSearchChange: (value: string) => void;
  onSelectApp: (appId: string) => void;
  selectedPlatform: InstallerPlatformGroup;
  selectedPlatformKey: string;
  onSelectPlatform: (platformKeyValue: string) => void;
  selectedArtifact: ReleaseDownloadCatalogItemRecord;
  selectedArtifactAlreadyAdded: boolean;
  latestSelected: boolean;
  onAddSelectedArtifact: () => void;
  onSelectVersion: (artifactId: string) => void;
}) {
  return (
    <div className="grid gap-3 rounded-md border bg-background p-3" data-testid="guest-download-installer-picker">
      <GuestAppSelector
        apps={apps}
        filteredApps={filteredApps}
        selectedAppId={selectedAppId}
        search={appSearch}
        onSearchChange={onAppSearchChange}
        onSelect={onSelectApp}
      />
      <GuestPlatformSelector
        platforms={apps.find((app) => app.appId === selectedAppId)?.platforms ?? apps[0]?.platforms ?? []}
        selectedPlatformKey={selectedPlatformKey}
        onSelect={onSelectPlatform}
      />
      <GuestArtifactSummary item={selectedArtifact} latestSelected={latestSelected} />
      <details className="rounded-md border bg-muted/40 p-3">
        <summary className="cursor-pointer text-sm font-medium">Дополнительно: выбрать версию</summary>
        <GuestVersionSelector
          platform={selectedPlatform}
          selectedArtifactId={selectedArtifact.artifact.id}
          latestArtifactId={selectedPlatform.latest.artifact.id}
          onSelect={onSelectVersion}
        />
      </details>
      <Button
        type="button"
        variant={selectedArtifactAlreadyAdded ? 'secondary' : 'outline'}
        disabled={selectedArtifactAlreadyAdded}
        onClick={onAddSelectedArtifact}
      >
        <Plus className="h-4 w-4" />
        {selectedArtifactAlreadyAdded ? 'Уже добавлено' : 'Добавить в гостевой доступ'}
      </Button>
    </div>
  );
}

function GuestAppSelector({
  apps,
  filteredApps,
  selectedAppId,
  search,
  onSearchChange,
  onSelect,
}: {
  apps: InstallerAppGroup[];
  filteredApps: InstallerAppGroup[];
  selectedAppId: string;
  search: string;
  onSearchChange: (value: string) => void;
  onSelect: (appId: string) => void;
}) {
  if (apps.length <= 1) {
    const app = apps[0];
    return (
      <div>
        <p className="text-xs font-medium text-muted-foreground">Программа</p>
        <p className="mt-1 flex items-center gap-2 text-sm font-semibold">
          {isMailArchiverApp(app?.appId) && <MailArchiverIcon className="h-5 w-5 [image-rendering:pixelated]" />}
          {app?.label ?? 'Программа'}
        </p>
      </div>
    );
  }

  return (
    <div className="grid gap-2">
      <Input value={search} onChange={(event) => onSearchChange(event.target.value)} placeholder="Найти программу" />
      <div className="grid gap-2">
        {filteredApps.map((app) => {
          const selected = app.appId === selectedAppId;
          return (
            <button
              key={app.appId}
              type="button"
              className={cn('rounded-md border bg-card p-3 text-left', selected && 'border-primary bg-primary/5')}
              onClick={() => onSelect(app.appId)}
            >
              <span className="flex items-center gap-2 text-sm font-semibold">
                {isMailArchiverApp(app.appId) && <MailArchiverIcon className="h-5 w-5 [image-rendering:pixelated]" />}
                {app.label}
              </span>
              <span className="mt-1 block text-xs text-muted-foreground">
                {app.platforms.length} платформ{app.platforms.length === 1 ? 'а' : 'ы'}
              </span>
            </button>
          );
        })}
        {filteredApps.length === 0 && (
          <div className="rounded-md bg-muted p-3 text-sm text-muted-foreground">Программы не найдены.</div>
        )}
      </div>
    </div>
  );
}

function GuestPlatformSelector({
  platforms,
  selectedPlatformKey,
  onSelect,
}: {
  platforms: InstallerPlatformGroup[];
  selectedPlatformKey: string;
  onSelect: (platformKeyValue: string) => void;
}) {
  return (
    <div className="grid gap-2">
      <p className="text-xs font-medium text-muted-foreground">Платформа</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {platforms.map((platform) => {
          const selected = platform.key === selectedPlatformKey;
          return (
            <button
              key={platform.key}
              type="button"
              className={cn(
                'rounded-md border bg-card px-3 py-2 text-left text-sm font-medium',
                selected && 'border-primary bg-primary/5',
              )}
              onClick={() => onSelect(platform.key)}
            >
              {platform.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function GuestVersionSelector({
  platform,
  selectedArtifactId,
  latestArtifactId,
  onSelect,
}: {
  platform: InstallerPlatformGroup;
  selectedArtifactId: string;
  latestArtifactId: string;
  onSelect: (artifactId: string) => void;
}) {
  return (
    <div className="mt-3 grid gap-2">
      {platform.items.map((item) => {
        const selected = item.artifact.id === selectedArtifactId;
        const latest = item.artifact.id === latestArtifactId;
        return (
          <button
            key={item.artifact.id}
            type="button"
            className={cn('rounded-md border bg-background p-3 text-left', selected && 'border-primary bg-primary/5')}
            onClick={() => onSelect(item.artifact.id)}
          >
            <span className="flex flex-wrap items-center gap-2 text-sm font-medium">
              {item.version}
              <Badge variant={item.channel.toLowerCase() === 'stable' ? 'visit' : 'outline'}>{item.channel}</Badge>
              {latest && <Badge variant="secondary">последняя</Badge>}
            </span>
            <span className="mt-1 block break-words text-xs text-muted-foreground">
              {item.artifact.filename} · {formatBytes(item.artifact.sizeBytes)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function GuestArtifactSummary({
  item,
  latestSelected,
}: {
  item: ReleaseDownloadCatalogItemRecord;
  latestSelected: boolean;
}) {
  return (
    <div className="rounded-md bg-muted p-3">
      <div className="flex items-start gap-3">
        {isMailArchiverApp(item.appId) ? (
          <MailArchiverIcon className="mt-0.5 h-4 w-4 [image-rendering:pixelated]" />
        ) : (
          <PackageCheck className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <div className="min-w-0 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium">{releaseCatalogItemLabel(item)}</p>
            {latestSelected && <Badge variant="visit">последняя</Badge>}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {item.artifact.filename} · {formatBytes(item.artifact.sizeBytes)}
          </p>
        </div>
      </div>
    </div>
  );
}

function SelectedInstallerList({
  items,
  onRemove,
}: {
  items: ReleaseDownloadCatalogItemRecord[];
  onRemove: (artifactId: string) => void;
}) {
  return (
    <div className="grid gap-2" data-testid="guest-download-selected-installers">
      <p className="text-xs font-medium text-muted-foreground">Будет открыто гостю</p>
      {items.length === 0 ? (
        <div className="rounded-md bg-muted p-3 text-sm text-muted-foreground">Добавьте установщик из выбора выше.</div>
      ) : (
        items.map((item) => (
          <div key={item.artifact.id} className="flex items-start gap-3 rounded-md border bg-background p-3">
            {isMailArchiverApp(item.appId) ? (
              <MailArchiverIcon className="mt-0.5 h-4 w-4 [image-rendering:pixelated]" />
            ) : (
              <PackageCheck className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            )}
            <div className="min-w-0 flex-1 text-sm">
              <p className="font-medium">{releaseCatalogItemLabel(item)}</p>
              <p className="mt-1 break-words text-xs text-muted-foreground">
                {item.artifact.filename} · {formatBytes(item.artifact.sizeBytes)}
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label="Убрать установщик"
              onClick={() => onRemove(item.artifact.id)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))
      )}
    </div>
  );
}

function normalizeCode(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, '');
}
